import { defineStore } from "pinia";

import usersApi from "@/services/api/usersApi";
import { usePermissionsStore } from "@/stores/permissionsStore";

import {
  clearSavedUserData,
  getUserId,
  getCompanyId,
  saveUserData,
} from "@/utils/userUtils";

export const useUserDataStore = defineStore("userData", {
  state: () => ({
    userData: null,
    isLoading: false,
    isSwitchingCompany: false,
  }),

  getters: {
    initials: (state) => state.userData?.initials || "",
    currentCompany: (state) =>
      state.userData?.current_company || null,
    companies: (state) => state.userData?.companies || [],

    pagePermissions() {
      const permissions =
        this.userData?.current_company_user?.permission_role
          ?.permissions || [];

      const result = {};
      permissions.forEach((p) => {
        if (p.target_resource?.startsWith("Pages::")) {
          const key = p.target_resource.replace("Pages::", "");
          result[key] =
            p.permissions?.find((a) => a.action === "visible")
              ?.value ?? true;
        }
      });
      return result;
    },
  },

  actions: {
    // Header и уведомления меняют контекст одинаково; после успеха нужен full reload.
    async switchCompany(companyId) {
      if (this.isSwitchingCompany || !companyId) return false;
      const userId = getUserId();
      const previousCompanyId = getCompanyId();
      if (!userId) return false;

      this.isSwitchingCompany = true;
      try {
        const response = await usersApi.changeCurrentCompany(companyId);
        const user = response.data.result;
        // Logout или смена сессии во время PATCH не должны восстановить старый профиль.
        if (getUserId() !== userId || getCompanyId() !== previousCompanyId ||
            user.id !== userId || user.current_company?.id !== companyId) return false;

        usePermissionsStore().clearPermissions();
        this.setUserData(user);
        return true;
      } catch {
        // Ошибку показывает общий interceptor, неподтверждённый контекст не применяем.
        return false;
      } finally {
        this.isSwitchingCompany = false;
      }
    },

    async loadUserData(forceUpdate = false) {
      if (this.userData && !forceUpdate) return;

      this.isLoading = true;

      try {
        // current_company хранится на backend и может измениться в другой сессии.
        // Перед company-scoped запросами подтверждаем сохранённый профиль сервером.
        const response = await usersApi.currentUser();
        this.setUserData(response.data.result);
      } catch (error) {
        console.error("Ошибка загрузки данных пользователя:", error);
      } finally {
        this.isLoading = false;
      }
    },

    setUserData(userData) {
      this.userData = userData;
      saveUserData(userData);
    },

    clearUserData() {
      this.userData = null;
      clearSavedUserData();
    },
  },
});
